import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { markNotificationsByBucketAsRead } from '../../utils/notifications';
import { FileText, PenTool, AlertCircle, ArrowRight, Clock, Bell } from 'lucide-react';
import toast from 'react-hot-toast';

export default function TenantContracts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [amendmentNotifs, setAmendmentNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadContracts();
      loadAmendmentNotifications().finally(() => {
        markNotificationsByBucketAsRead(user.uid, 'contracts');
      });
    }
  }, [user]);

  const loadContracts = async () => {
    if (!user) return;
    try {
      setLoading(true);

      const contractsQuery1 = query(
        collection(db, 'contracts'),
        where('tenantID', '==', user.uid)
      );
      const contractsQuery2 = query(
        collection(db, 'contracts'),
        where('tenantId', '==', user.uid)
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(contractsQuery1).catch(() => ({ docs: [] })),
        getDocs(contractsQuery2).catch(() => ({ docs: [] }))
      ]);

      const allDocs = [...snapshot1.docs, ...snapshot2.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) =>
        index === self.findIndex(d => d.id === doc.id)
      );

      const allContracts = uniqueDocs.map(doc => ({ id: doc.id, ...doc.data() }));
      const validStatuses = ['fullySigned', 'signedByTenant', 'sentToTenant', 'signedByLandlord', 'archived'];
      const filtered = allContracts.filter(c => validStatuses.includes(c.status));

      const active = filtered.filter(c => c.status !== 'archived');
      const archived = filtered.filter(c => c.status === 'archived');
      setContracts([...active, ...archived]);
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Fout bij het laden van contracten');
    } finally {
      setLoading(false);
    }
  };

  const loadAmendmentNotifications = async () => {
    if (!user) return;
    try {
      const AMENDMENT_TYPES = ['amendment_proposed', 'contract_updated', 'contract_sent'];

      const recipientUnreadQ = query(
        collection(db, 'notifications'),
        where('recipientUserId', '==', user.uid),
        where('read', '==', false)
      );
      const tenantUnreadQ = query(
        collection(db, 'notifications'),
        where('tenantId', '==', user.uid),
        where('read', '==', false)
      );

      const [recipientSnap, tenantSnap] = await Promise.all([
        getDocs(recipientUnreadQ).catch(() => ({ docs: [] })),
        getDocs(tenantUnreadQ).catch(() => ({ docs: [] })),
      ]);

      const seen = new Set();
      const allDocs = [...recipientSnap.docs, ...tenantSnap.docs].filter((d) => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      });

      const amendments = allDocs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => AMENDMENT_TYPES.includes(n.type));

      setAmendmentNotifs(amendments);
    } catch (err) {
      console.warn('Could not load amendment notifications:', err);
    }
  };

  const statusColors = {
    'sentToTenant': 'bg-orange-100 text-orange-800',
    'signedByTenant': 'bg-blue-100 text-blue-800',
    'signedByLandlord': 'bg-purple-100 text-purple-800',
    'fullySigned': 'bg-green-100 text-green-800',
    'archived': 'bg-gray-100 text-gray-500',
  };
  const statusLabels = {
    'sentToTenant': 'Wacht op ondertekening',
    'signedByTenant': 'Getekend door jou',
    'signedByLandlord': 'Getekend door verhuurder',
    'fullySigned': 'Volledig getekend',
    'archived': 'Gearchiveerd',
  };

  return (
    <div className="container-professional py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Contracten</h1>
        <p className="text-gray-600">Bekijk al je huurcontracten</p>
      </div>

      {/* Amendment proposals */}
      {amendmentNotifs.length > 0 && (
        <div className="mb-6 space-y-3">
          {amendmentNotifs.map((notif) => (
            <div
              key={notif.id}
              className="relative overflow-hidden rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-white to-blue-50 p-6 shadow-sm"
            >
              <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-purple-500 to-blue-500"></div>
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 shadow-lg">
                  <PenTool className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900">{notif.title || 'Contractwijziging voorgesteld'}</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                      <AlertCircle className="w-3 h-3" />
                      Actie vereist
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{notif.message}</p>
                  {notif.createdAt?.seconds && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mb-3">
                      <Clock className="w-3 h-3" />
                      {new Date(notif.createdAt.seconds * 1000).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                  <button
                    onClick={() => {
                      if (notif.actionUrl) {
                        navigate(notif.actionUrl);
                      } else if (notif.contractId && notif.amendmentId) {
                        navigate(`/contract/${notif.contractId}/amendment/${notif.amendmentId}`);
                      } else if (notif.contractId) {
                        navigate(`/contract/${notif.contractId}`);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow-sm"
                  >
                    Bekijk & Reageer
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Contracten laden...</p>
          </div>
        </div>
      ) : contracts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Geen contracten</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Je hebt nog geen contracten. Contracten worden hier getoond zodra je een kot huurt.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => {
            const isArchived = contract.status === 'archived';
            const isEdited = contract.isEditedVersion;

            return (
              <div key={contract.id} className={`rounded-2xl shadow-sm border p-6 transition-shadow ${
                isArchived
                  ? 'bg-gray-50 border-gray-200 opacity-75'
                  : 'bg-white border-gray-200 hover:shadow-md'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {contract.propertyTitle || contract.contractNumber || 'Contract'}
                      </h3>
                      {contract.amendmentState === 'proposed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold animate-pulse">
                          <Bell className="w-3 h-3" />
                          Wijziging voorgesteld
                        </span>
                      )}
                      {contract.amendmentState === 'tenantAccepted' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                          <Clock className="w-3 h-3" />
                          Wacht op verhuurder
                        </span>
                      )}
                      {isEdited && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          <PenTool className="w-3 h-3" />
                          Gewijzigde versie
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Contractnummer: {contract.contractNumber || contract.id}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[contract.status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusLabels[contract.status] || contract.status}
                  </span>
                </div>

                {isArchived && contract.replacedByContractId && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                    <AlertCircle className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="text-blue-800">Dit contract is vervangen door een nieuwe versie.</span>
                    <button
                      onClick={() => navigate(`/contract/${contract.replacedByContractId}`)}
                      className="ml-auto text-blue-600 font-semibold hover:text-blue-800 flex items-center gap-1"
                    >
                      Bekijk nieuw contract <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Maandelijkse huur</p>
                    <p className="font-semibold text-gray-900">€{contract.monthlyRent || contract.core?.rent || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Waarborg</p>
                    <p className="font-semibold text-gray-900">€{contract.deposit || contract.core?.deposit || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Startdatum</p>
                    <p className="font-semibold text-gray-900">
                      {(() => {
                        const date = contract.startDate?.toDate ? contract.startDate.toDate() :
                                     (contract.startDate instanceof Date ? contract.startDate :
                                     (contract.core?.startDate?.toDate ? contract.core.startDate.toDate() :
                                     (contract.core?.startDate instanceof Date ? contract.core.startDate : null)));
                        return date ? date.toLocaleDateString('nl-NL') : 'N/A';
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Einddatum</p>
                    <p className="font-semibold text-gray-900">
                      {(() => {
                        const date = contract.endDate?.toDate ? contract.endDate.toDate() :
                                     (contract.endDate instanceof Date ? contract.endDate :
                                     (contract.core?.endDate?.toDate ? contract.core.endDate.toDate() :
                                     (contract.core?.endDate instanceof Date ? contract.core.endDate : null)));
                        return date ? date.toLocaleDateString('nl-NL') : 'N/A';
                      })()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500">
                    {contract.createdAt?.toDate && (
                      <span>Aangemaakt: {contract.createdAt.toDate().toLocaleDateString('nl-NL')}</span>
                    )}
                    {contract.updatedAt?.toDate && contract.amendmentState === 'applied' && (
                      <span className="ml-3 text-blue-600 font-medium">
                        Gewijzigd: {contract.updatedAt.toDate().toLocaleDateString('nl-NL')}
                      </span>
                    )}
                    {contract.archivedAt?.toDate && (
                      <span className="ml-3 text-gray-400">
                        Gearchiveerd: {contract.archivedAt.toDate().toLocaleDateString('nl-NL')}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/contract/${contract.id}`)}
                    className={`px-4 py-2 rounded-xl transition-colors flex items-center space-x-2 text-sm font-medium ${
                      isArchived
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-[#5EABEB] text-white hover:bg-[#4A98D6]'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>{isArchived ? 'Bekijk Oud Contract' : 'Bekijk Contract'}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

